import {
  MeasureOneExperienceType as MeasureOneExperienceTypeProto,
  MeasureOneSourceDataType as MeasureOneSourceDataTypeProto,
  MeasureOneJobTaxDataImportStatus as StatusProto,
  type MeasureOneJobTaxDataImport as MeasureOneJobTaxDataImportProto,
  type MeasureOneDataRequest as MeasureOneDataRequestProto,
  DataRequestStatus as MeasureOneDataRequestStatusProto,
  type MeasureOneItem as MeasureOneItemProto,
} from '@taxfyle/api-internal/internal/measure_one_pb'
import type { JobTaxDataImportStatus } from '@/features/jobs/details/tax-type/info-gathering/measure-one/job-tax-data-import-status'
import { safeTimestampToDate } from '@/utils/grpc-util'
import type { MeasureOneDataRequestStatus } from '@/features/jobs/details/tax-type/info-gathering/measure-one/data-request-status'
import type {
  JobTaxDataImportProps,
  MeasureOneDataRequest,
} from '@/features/jobs/details/tax-type/info-gathering/measure-one/JobTaxDataImport'

export enum MeasureOneExperience {
  Employment = 'Employment',
  BankBrokerage = 'BankBrokerage',
}

export enum WellKnownDocumentTypeShortId {
  F1098 = '1098',
  F1099B = '1099_B',
  F1099Consolidated = '1099_COMPOSITE',
  F1099Div = '1099_DIV',
  F1099Int = '1099_INT',
  F1099Misc = '1099_MISC',
  F1099Nec = '1099_NEC',
  F1099Oid = '1099_OID',
  FW2 = 'W2',
  Unspecified = 'UNSPECIFIED',
}

export const MEASURE_ONE_SUPPORTED_DOCUMENT_TYPES = new Set<string>(
  Object.values(WellKnownDocumentTypeShortId),
)

export function mapMeasureOneExperienceToProtos(
  experience: MeasureOneExperience,
) {
  switch (experience) {
    case MeasureOneExperience.BankBrokerage:
      return MeasureOneExperienceTypeProto.MEASURE_ONE_EXPERIENCE_TYPE_BANK_BROKERAGE
    case MeasureOneExperience.Employment:
      return MeasureOneExperienceTypeProto.MEASURE_ONE_EXPERIENCE_TYPE_EMPLOYMENT
    default:
      return MeasureOneExperienceTypeProto.MEASURE_ONE_EXPERIENCE_TYPE_UNSPECIFIED
  }
}

export function mapMeasureOneSourceDataToProtos(
  documentTypeShortId: WellKnownDocumentTypeShortId,
) {
  switch (documentTypeShortId) {
    case WellKnownDocumentTypeShortId.F1099B:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_B
    case WellKnownDocumentTypeShortId.F1099Consolidated:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_CONSOLIDATED
    case WellKnownDocumentTypeShortId.F1099Div:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_DIV
    case WellKnownDocumentTypeShortId.F1099Int:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_INT
    case WellKnownDocumentTypeShortId.FW2:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_W2
    case WellKnownDocumentTypeShortId.F1098:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1098
    case WellKnownDocumentTypeShortId.F1099Nec:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_NEC
    case WellKnownDocumentTypeShortId.F1099Oid:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_OID
    case WellKnownDocumentTypeShortId.F1099Misc:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_MISC
    default:
      return MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_UNSPECIFIED
  }
}

export function mapJobTaxDataImportStatus(
  status: StatusProto,
): JobTaxDataImportStatus {
  switch (status) {
    case StatusProto.MEASURE_ONE_JOB_TAX_DATA_IMPORT_STATUS_INITIALIZED:
      return 'Initialized'
    case StatusProto.MEASURE_ONE_JOB_TAX_DATA_IMPORT_STATUS_DATA_REQUEST_IN_PROGRESS:
      return 'DataRequestInProgress'
    case StatusProto.MEASURE_ONE_JOB_TAX_DATA_IMPORT_STATUS_DATA_REQUEST_COMPLETED:
      return 'DataRequestCompleted'
    case StatusProto.MEASURE_ONE_JOB_TAX_DATA_IMPORT_STATUS_DOCUMENTS_UPLOAD_REQUESTED:
      return 'DocumentsUploadRequested'
    case StatusProto.MEASURE_ONE_JOB_TAX_DATA_IMPORT_STATUS_DOCUMENTS_UPLOAD_COMPLETED:
      return 'DocumentsUploadCompleted'
    default:
      return 'Unspecified'
  }
}

export function mapMeasureOneDataRequestStatus(
  status: MeasureOneDataRequestStatusProto,
): MeasureOneDataRequestStatus {
  switch (status) {
    case MeasureOneDataRequestStatusProto.DATA_REQUEST_STATUS_STARTED:
      return 'Started'
    case MeasureOneDataRequestStatusProto.DATA_REQUEST_STATUS_CONNECTION_COMPLETED:
      return 'ConnectionCompleted'
    case MeasureOneDataRequestStatusProto.DATA_REQUEST_STATUS_ITEMS_PROCESSED:
      return 'ItemsProcessed'
    case MeasureOneDataRequestStatusProto.DATA_REQUEST_STATUS_EMPTY:
      return 'Empty'
    default:
      return 'Unspecified'
  }
}

export function mapTaxJobDataImport(
  jobTaxDataImport: MeasureOneJobTaxDataImportProto,
): JobTaxDataImportProps {
  return {
    id: jobTaxDataImport.getId(),
    jobId: jobTaxDataImport.getJobId(),
    workspaceId: jobTaxDataImport.getWorkspaceId(),
    status: mapJobTaxDataImportStatus(jobTaxDataImport.getStatus()),
    individualId: jobTaxDataImport.getIndividualId(),
    dataRequests: jobTaxDataImport
      .getDataRequestsList()
      .map(mapMeasureOneDataRequest),
    dateCreated: safeTimestampToDate(jobTaxDataImport.getDateCreated()),
    dateModified: safeTimestampToDate(jobTaxDataImport.getDateModified()),
  }
}

export function mapMeasureOneDataRequest(
  dataRequest: MeasureOneDataRequestProto,
): MeasureOneDataRequest {
  return {
    id: dataRequest.getId(),
    status: mapMeasureOneDataRequestStatus(dataRequest.getStatus()),
    experienceId: dataRequest.getExperienceId(),
    requestedByUserPublicId: dataRequest.getRequestedByUserPublicId(),
    dateStarted: safeTimestampToDate(dataRequest.getDateStarted()),
    dataSource: {
      id: dataRequest.getDataSource()?.getId() ?? '',
      name: dataRequest.getDataSource()?.getName() ?? '',
    },
    dateConnectionCompleted: safeTimestampToDate(
      dataRequest.getDateConnectionCompleted(),
    ),
    dateItemsProcessed: safeTimestampToDate(
      dataRequest.getDateItemsProcessed(),
    ),
    items: dataRequest.getItemsList().map(mapMeasureOneItem),
  }
}

export function mapMeasureOneItem(item: MeasureOneItemProto) {
  return {
    id: item.getId(),
    sourceDataType: mapMeasureOneSourceDataTypeFromProto(
      item.getSourceDataType(),
    ),
  }
}

function mapMeasureOneSourceDataTypeFromProto(
  dataType: MeasureOneSourceDataTypeProto,
) {
  switch (dataType) {
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1098:
      return WellKnownDocumentTypeShortId.F1098
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_B:
      return WellKnownDocumentTypeShortId.F1099B
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_CONSOLIDATED:
      return WellKnownDocumentTypeShortId.F1099Consolidated
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_DIV:
      return WellKnownDocumentTypeShortId.F1099Div
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_INT:
      return WellKnownDocumentTypeShortId.F1099Int
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_MISC:
      return WellKnownDocumentTypeShortId.F1099Misc
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_NEC:
      return WellKnownDocumentTypeShortId.F1099Nec
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_1099_OID:
      return WellKnownDocumentTypeShortId.F1099Oid
    case MeasureOneSourceDataTypeProto.MEASURE_ONE_SOURCE_DATA_TYPE_FORM_W2:
      return WellKnownDocumentTypeShortId.FW2
    default:
      console.warn(`Unknown MeasureOne source data type: ${dataType}`)
      return WellKnownDocumentTypeShortId.Unspecified
  }
}
